import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { OpcoesComissoes, BotaoEditarProtocolo, HistoricoTramitacao } from '../components/ProtocoloCorrecoes'; // PLENARIUM:PROTOCOLO_CORRECOES PLENARIUM:PROTOCOLO_COMISSOES
import { LogOut, Plus, X, FileText, ArrowRightLeft, Search, Paperclip, Clock, Archive, ChevronDown, ChevronUp } from 'lucide-react';

const VAZIO = {
  tipo: 'ENTRADA',
  assunto: '',
  descricao: '',
  remetente: '',
  destinatario: '',
  setor: '',
  arquivoUrl: ''
};

const SETORES = ['Presidência', 'Mesa Diretora', 'Secretaria', 'Jurídico', 'Financeiro', 'Plenário', 'Protocolo', 'Arquivo'];

const STATUS_LABEL = {
  PENDENTE: { label: 'Pendente', cor: 'badge-amarelo' },
  EM_TRAMITACAO: { label: 'Em Tramitação', cor: 'badge-azul' },
  CONCLUIDO: { label: 'Concluído', cor: 'badge-verde' },
  ARQUIVADO: { label: 'Arquivado', cor: 'badge-cinza' },
};

const TIPO_LABEL = {
  ENTRADA: { label: '📥 Entrada', cor: 'badge-azul' },
  SAIDA: { label: '📤 Saída', cor: 'badge-amarelo' },
};

// PLENARIUM:ANEXO_PROTOCOLO -- o Chrome bloqueia abrir um "data:" direto
// (tela em branco: "Not allowed to navigate top frame to data URL").
// O arquivo vira Blob e abre numa aba nova; se o navegador bloquear, baixa.
function abrirArquivo(url) {
  if (!url) return;
  if (!String(url).startsWith('data:')) { window.open(url, '_blank', 'noopener'); return; }
  try {
    const [cab, corpo] = url.split(',');
    const tipo = (cab.match(/:(.*?);/) || [])[1] || 'application/octet-stream';
    const bin = atob(corpo); const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blobUrl = URL.createObjectURL(new Blob([bytes], { type: tipo }));
    const aba = window.open(blobUrl, '_blank');
    if (!aba) { const a = document.createElement('a'); a.href = blobUrl; a.download = 'anexo-protocolo'; document.body.appendChild(a); a.click(); a.remove(); }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
  } catch (e) { window.alert('Não foi possível abrir o arquivo anexado.'); }
}

export default function PainelOperador() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();

  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [modalTramitar, setModalTramitar] = useState(false);
  const [protocoloSelecionado, setProtocoloSelecionado] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [form, setForm] = useState(VAZIO);
  const [formTramitar, setFormTramitar] = useState({ setor: '', observacao: '', status: 'EM_TRAMITACAO' });
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroStatus) params.status = filtroStatus;
      if (busca) params.busca = busca;
      const r = await axios.get('/api/protocolos', { params });
      setLista(r.data);
    } catch { toast.error('Erro ao carregar protocolos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, [filtroTipo, filtroStatus]);

  const handleBusca = (e) => {
    e.preventDefault();
    carregar();
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await axios.post('/api/protocolos', form);
      toast.success('Protocolo registrado!');
      setModal(false);
      setForm(VAZIO);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao registrar protocolo');
    } finally { setSalvando(false); }
  };

  const tramitar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await axios.post(`/api/protocolos/${protocoloSelecionado.id}/tramitar`, formTramitar);
      toast.success('Protocolo tramitado!');
      setModalTramitar(false);
      setFormTramitar({ setor: '', observacao: '', status: 'EM_TRAMITACAO' });
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao tramitar');
    } finally { setSalvando(false); }
  };

  const arquivar = async (id, numero) => {
    if (!window.confirm(`Arquivar protocolo ${numero}?`)) return;
    try {
      await axios.patch(`/api/protocolos/${id}/status`, { status: 'ARQUIVADO' });
      toast.success('Protocolo arquivado!');
      carregar();
    } catch { toast.error('Erro ao arquivar'); }
  };

  const anexarArquivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo deve ter no máximo 5MB!');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, arquivoUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const handleLogout = () => {
    logout();
    navigate('/login/operador');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--fundo)' }}>
      {/* Header */}
      <div style={{ background: '#1a3a5c', color: '#fff', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>📋 Protocolo Digital</h1>
          <p style={{ fontSize: '0.78rem', opacity: 0.7, margin: 0 }}>{usuario?.camara?.nome} — {usuario?.nome}</p>
        </div>
        <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
          <LogOut size={15} /> Sair
        </button>
      </div>

      <div style={{ padding: 24 }}>
        {/* Topo */}
        <div className="page-header">
          <div>
            <h2 className="page-title">Protocolos</h2>
            <p className="page-subtitle">{lista.length} protocolo(s) encontrado(s)</p>
          </div>
          <button className="btn btn-primary" onClick={() => { setForm(VAZIO); setModal(true); }}>
            <Plus size={16} /> Novo Protocolo
          </button>
        </div>

        {/* Filtros */}
        <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <form onSubmit={handleBusca} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}>
              <input
                className="form-input"
                placeholder="Buscar por número, assunto ou remetente..."
                value={busca}
                onChange={e => setBusca(e.target.value)}
                style={{ flex: 1 }}
              />
              <button className="btn btn-secondary" type="submit"><Search size={15} /></button>
            </form>
            <select className="form-input form-select" style={{ width: 140 }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
              <option value="">Todos os tipos</option>
              <option value="ENTRADA">📥 Entrada</option>
              <option value="SAIDA">📤 Saída</option>
            </select>
            <select className="form-input form-select" style={{ width: 160 }} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
              <option value="">Todos os status</option>
              <option value="PENDENTE">Pendente</option>
              <option value="EM_TRAMITACAO">Em Tramitação</option>
              <option value="CONCLUIDO">Concluído</option>
              <option value="ARQUIVADO">Arquivado</option>
            </select>
          </div>
        </div>

        {/* Lista */}
        <div className="card">
          {loading ? (
            <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>
          ) : lista.length === 0 ? (
            <div className="sem-dados"><p>Nenhum protocolo encontrado.</p></div>
          ) : lista.map(p => (
            <div key={p.id} style={{ borderBottom: '1px solid var(--cinza)', padding: '14px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>{p.numero}</strong>
                    <span className={`badge ${TIPO_LABEL[p.tipo]?.cor}`}>{TIPO_LABEL[p.tipo]?.label}</span>
                    <span className={`badge ${STATUS_LABEL[p.status]?.cor}`}>{STATUS_LABEL[p.status]?.label}</span>
                    {p.arquivoUrl && <Paperclip size={13} style={{ color: 'var(--texto-suave)' }} />}
                  </div>
                  <p style={{ fontWeight: 600, marginBottom: 2 }}>{p.assunto}</p>
                  <p style={{ fontSize: '0.8rem', color: 'var(--texto-suave)' }}>
                    De: <strong>{p.remetente}</strong> → Para: <strong>{p.destinatario}</strong> | Setor: {p.setor}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', marginTop: 2 }}>
                    <Clock size={11} style={{ marginRight: 3 }} />
                    {new Date(p.criadoEm).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {p.arquivoUrl && (
                    <button type="button" onClick={() => abrirArquivo(p.arquivoUrl)} className="btn btn-secondary btn-sm">{/* PLENARIUM:ANEXO_PROTOCOLO */}
                      <Paperclip size={13} /> Ver arquivo
                    </button>
                  )}
                  {p.status !== 'ARQUIVADO' && (
                    <>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setProtocoloSelecionado(p); setModalTramitar(true); }}>
                        <ArrowRightLeft size={13} /> Tramitar
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => arquivar(p.id, p.numero)}>
                        <Archive size={13} />
                      </button>
                    </>
                  )}
                  <BotaoEditarProtocolo protocolo={p} onSalvo={carregar} /> {/* PLENARIUM:PROTOCOLO_CORRECOES */}
                  <button className="btn btn-secondary btn-sm" onClick={() => setExpandido(expandido === p.id ? null : p.id)}>
                    {expandido === p.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </button>
                </div>
              </div>

              {expandido === p.id && <HistoricoTramitacao protocolo={p} onMudou={carregar} />} {/* PLENARIUM:PROTOCOLO_CORRECOES */}
            </div>
          ))}
        </div>
      </div>

      {/* Modal Novo Protocolo */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2 className="modal-title"><FileText size={18} /> Novo Protocolo</h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={salvar}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select className="form-input form-select" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} required>
                    <option value="ENTRADA">📥 Entrada</option>
                    <option value="SAIDA">📤 Saída</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Setor Atual *</label>
                  <select className="form-input form-select" value={form.setor} onChange={e => setForm({ ...form, setor: e.target.value })} required>
                    <option value="">Selecione</option>
                    {SETORES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Assunto *</label>
                  <input className="form-input" value={form.assunto} onChange={e => setForm({ ...form, assunto: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Remetente *</label>
                  <input className="form-input" value={form.remetente} onChange={e => setForm({ ...form, remetente: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Destinatário *</label>
                  <input className="form-input" value={form.destinatario} onChange={e => setForm({ ...form, destinatario: e.target.value })} required />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Descrição</label>
                  <textarea className="form-input" rows={3} value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} style={{ resize: 'vertical' }} />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Anexar PDF (máx. 5MB)</label>
                  <input type="file" accept=".pdf,image/*" className="form-input" onChange={anexarArquivo} />
                  {form.arquivoUrl && <p style={{ fontSize: '0.78rem', color: 'var(--verde)', marginTop: 4 }}>✅ Arquivo anexado</p>}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : 'Registrar Protocolo'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Tramitar */}
      {modalTramitar && protocoloSelecionado && (
        <div className="modal-overlay" onClick={() => setModalTramitar(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><ArrowRightLeft size={18} /> Tramitar — {protocoloSelecionado.numero}</h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setModalTramitar(false)}><X size={20} /></button>
            </div>
            <form onSubmit={tramitar}>
              <div className="form-group">
                <label className="form-label">Encaminhar para o Setor *</label>
                <select className="form-input form-select" value={formTramitar.setor} onChange={e => setFormTramitar({ ...formTramitar, setor: e.target.value })} required>
                  <option value="">Selecione</option>
                  {SETORES.map(s => <option key={s} value={s}>{s}</option>)}
                  <OpcoesComissoes /> {/* PLENARIUM:PROTOCOLO_COMISSOES */}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status *</label>
                <select className="form-input form-select" value={formTramitar.status} onChange={e => setFormTramitar({ ...formTramitar, status: e.target.value })}>
                  <option value="EM_TRAMITACAO">Em Tramitação</option>
                  <option value="CONCLUIDO">Concluído</option>
                  <option value="ARQUIVADO">Arquivado</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Observação</label>
                <textarea className="form-input" rows={3} value={formTramitar.observacao} onChange={e => setFormTramitar({ ...formTramitar, observacao: e.target.value })} style={{ resize: 'vertical' }} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalTramitar(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : 'Tramitar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}